import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaPlatformLocationFeatureV1Service, serviceName } from '../service';
import { CheckLocationNotificationPreferenceIO, ServiceQueries } from '../types';
import { usePlatformLocationFeatureV1Queries } from './use-platform-location-feature-v1-queries';
import {
  UsePlatformLocationFeatureV1QueryEndpointArgs,
  usePlatformLocationFeatureV1Query,
} from './use-platform-location-feature-v1-query';

/**
 * A hook that returns a query for the `CheckLocationNotificationPreference` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useCheckLocationNotificationPreferenceQuery = <
  E = unknown,
  D = CheckLocationNotificationPreferenceIO['output']
>(
  args: UsePlatformLocationFeatureV1QueryEndpointArgs<'CheckLocationNotificationPreference', E, D>
) =>
  usePlatformLocationFeatureV1Query({
    endpointName: 'CheckLocationNotificationPreference',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `CheckLocationNotificationPreference` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useCheckLocationNotificationPreferenceQueries = <
  E = unknown,
  D = CheckLocationNotificationPreferenceIO['output']
>(
  argsArr: UsePlatformLocationFeatureV1QueryEndpointArgs<'CheckLocationNotificationPreference', E, D>[]
) =>
  usePlatformLocationFeatureV1Queries(
    argsArr.map((args) => ({
      endpointName: 'CheckLocationNotificationPreference',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `CheckLocationNotificationPreference` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchCheckLocationNotificationPreference = () => {
  const queryClient = useQueryClient();

  const fetchCheckLocationNotificationPreference = async <
    E = unknown,
    D = CheckLocationNotificationPreferenceIO['output']
  >({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UsePlatformLocationFeatureV1QueryEndpointArgs<'CheckLocationNotificationPreference', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      CheckLocationNotificationPreferenceIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'CheckLocationNotificationPreference'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        CheckLocationNotificationPreferenceIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'CheckLocationNotificationPreference'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'CheckLocationNotificationPreference'>({
          serviceName,
          endpointName: 'CheckLocationNotificationPreference',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaPlatformLocationFeatureV1Service.CheckLocationNotificationPreference(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchCheckLocationNotificationPreference;
};
